import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
    AnalyticsService,
    CommerceService,
    ControlNumbersStatusService,
    FinaPaymentsIncomingService,
    NewsService,
    PaymentRenewalAlertService,
    PlansService,
    SalesChannelsService,
    ToastService,
} from '@common/services';
import { clearSupportSession, readSupportSession, storeSupportSession, SupportSessionInfo, User } from '@modules/auth/models';
import { UserService } from '@modules/auth/services';
// Import directo al archivo (no el barril): el vigía solo depende de CreditosService y UserService.
import { LineMilestoneWatcherService } from '@modules/creditos/services/line-milestone-watcher.service';
import { clearBannerHides } from '@modules/creditos/utils/banner-hide.util';
import { clearLoginModalChecked } from '@modules/creditos/utils/line-modal-views.util';
// Import directo al archivo (no el barril) para evitar el ciclo de DI:
// AuthService → OnboardingService → SocketService (genérico, en app-common).
import { OnboardingService } from '@modules/onboarding/services/onboarding.service';
import { OnboardingAnalyticsService } from '@modules/onboarding/services/onboarding-analytics.service';
import { casheaGroupProps } from '@modules/pos/services/cashea.service';
import { FiscalSellerStorageService } from '@modules/smart-api/services/fiscal-seller-storage.service';
import { IgtfPolicyService } from '@modules/smart-api/services/igtf-policy.service';
import { environment } from 'environments/environment';
import posthog from 'posthog-js';
import { Observable, of, throwError } from 'rxjs';
import { map, tap } from 'rxjs/operators';

/** Cuentas candidatas tras validar email+contraseña (login unificado / selector). */
export interface LoginAccountOption {
    _id: string;
    email: string;
    tenantId: string;
    companyName: string | null;
}

interface AuthResponseData {
    _id: string;
    email: string;
    name: string;
    lastName: string;
    tenantId: string;
    token: string;
    expiresIn: number;
    roles: string[];
    phone: string;
    birthday: Date;
    location: string;
    locations: string[];
    enabled: boolean;
    status: string;
    permissions: Object;
    showNews: boolean;
    instructionsNotToShow: string[];
    salePreferences: any;
    user_hash: string;
    // FINA-2062: gating del módulo Créditos. El backend los devuelve en signIn /
    // verified-user; el frontend los pasa al constructor del User para que el guard
    // del módulo y el sidebar item los lean.
    creditAccess?: boolean;
    isOwner?: boolean;
    instructionsViews?: { [instructionsSetName: string]: number };
}

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private tokenExpirationTimer: any;
    API_URL = environment.API_URL;

    constructor(
        private http: HttpClient,
        private router: Router,
        private userService: UserService,
        private commerceService: CommerceService,
        private toastService: ToastService,
        private plansService: PlansService,
        private salesChannelsService: SalesChannelsService,
        private newsService: NewsService,
        private analyticsService: AnalyticsService,
        private fiscalSellerStorageService: FiscalSellerStorageService,
        private paymentRenewalAlertService: PaymentRenewalAlertService,
        private onboardingService: OnboardingService,
        private onboardingAnalyticsService: OnboardingAnalyticsService,
        private lineMilestone: LineMilestoneWatcherService,
        private finaPaymentsIncomingService: FinaPaymentsIncomingService,
        private controlNumbersStatus: ControlNumbersStatusService,
        private igtfPolicyService: IgtfPolicyService,
    ) {
        // Add visibility change listener
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState !== 'visible') return;
            this.checkSession();
            // Re-hidratar la tasa BCV (y su token de versión) al volver a la app, para que
            // un comercio que operó al día siguiente no cotice con la tasa de ayer. Solo con
            // sesión activa; el cron mueve el updateDate a diario y el backend valida por token.
            if (this.userService.user$.value) this.commerceService.obtainExchangeRateBCV$();
        });
    }

    getAuth$(): Observable<{}> {
        return of({});
    }

    isTenant$(): Observable<any> {
        return this.http.get<any>(`${this.API_URL}/tenants/isTenant`);
    }

    /** True si el hostname actual está configurado como entrada de login sin tenant en la URL. */
    usesUnifiedLoginHost(): boolean {
        const host = window.location.hostname.toLowerCase();
        const list = (environment as { unifiedLoginHostnames?: string[] }).unifiedLoginHostnames;
        if (!list?.length) return false;
        return list.map(h => h.toLowerCase()).includes(host);
    }

    /**
     * Si el host es de login unificado y hay cookie de último tenant, redirige al subdominio del tenant.
     * `?relogin=1` o `?expired=true` borra la cookie y no redirige. Devuelve true si inició `location.replace`.
     */
    tryRedirectUnifiedLoginHostToLastTenant(): boolean {
        if (typeof window === 'undefined') return false;
        const params = new URLSearchParams(window.location.search);
        if (params.get('relogin') === '1') {
            this.clearLastTenantCookie();
            return false;
        }
        if (params.get('expired') === 'true') {
            this.clearLastTenantCookie();
            return false;
        }
        if (!this.usesUnifiedLoginHost()) return false;
        const tid = this.readLastTenantCookie();
        if (!tid) return false;
        const url = this.getTenantOriginAbsoluteUrl(tid);
        if (!url) return false;
        window.location.replace(url);
        return true;
    }

    /** URL absoluta del dashboard en el subdominio del tenant (siempre `/dashboard`). */
    getTenantDashboardAbsoluteUrl(tenantId: string): string | null {
        return this.buildTenantAbsoluteUrl(tenantId, '/dashboard');
    }

    /**
     * URL al tenant conservando path, query y hash actuales.
     * Si el path es vacío o `/`, usa `pathFallback` (por defecto `/dashboard`).
     */
    getTenantOriginAbsoluteUrl(tenantId: string, pathFallback = '/dashboard'): string | null {
        let path = typeof window !== 'undefined' ? `${window.location.pathname || ''}${window.location.search || ''}${window.location.hash || ''}` : pathFallback;
        if (path.startsWith('/http://') || path.startsWith('/https://')) {
            path = pathFallback;
        }
        if (!path || path === '/') path = pathFallback;
        return this.buildTenantAbsoluteUrl(tenantId, path);
    }

    private buildTenantAbsoluteUrl(tenantId: string, path: string): string | null {
        const tid = this.normalizeTenantIdForCookie(String(tenantId));
        if (!tid) return null;
        const base = (environment as { tenantSubdomainBaseUrl?: string }).tenantSubdomainBaseUrl?.trim();
        if (!base) return null;
        const tenantHost = `${tid}.${String(base).toLowerCase()}`;
        const protocol = environment.production ? 'https' : window.location.protocol.replace(':', '');
        const port = environment.production ? '' : window.location.port ? `:${window.location.port}` : '';
        const suffix = path.startsWith('/') ? path : `/${path}`;
        return `${protocol}://${tenantHost}${port}${suffix}`;
    }

    setLastTenantCookie(tenantId: string): void {
        const name = (environment as { lastTenantCookieName?: string }).lastTenantCookieName;
        const domain = this.getLastTenantCookieDomain();
        if (!name || !domain || typeof document === 'undefined') return;
        const normalized = this.normalizeTenantIdForCookie(String(tenantId));
        if (!normalized) return;
        const maxAgeDays = (environment as { lastTenantCookieMaxAgeDays?: number }).lastTenantCookieMaxAgeDays ?? 30;
        const maxAge = Math.max(1, maxAgeDays) * 24 * 60 * 60;
        const secure = environment.production || window.location.protocol === 'https:' ? '; Secure' : '';
        document.cookie = `${name}=${encodeURIComponent(normalized)}; Domain=${domain}; Path=/; Max-Age=${maxAge}${secure}; SameSite=Lax`;
    }

    clearLastTenantCookie(): void {
        const name = (environment as { lastTenantCookieName?: string }).lastTenantCookieName;
        if (!name || typeof document === 'undefined') return;
        const expire = '; Expires=Thu, 01 Jan 1970 00:00:00 GMT';
        const sameSite = '; SameSite=Lax';
        const https = environment.production || window.location.protocol === 'https:';

        const wipe = (suffix: string) => {
            document.cookie = `${name}=; Path=/; Max-Age=0${expire}${sameSite}${suffix}`;
        };
        wipe(https ? '; Secure' : '');
        wipe('; Secure');
        wipe('');

        const domain = this.getLastTenantCookieDomain();
        const domains = new Set<string>();
        if (domain) domains.add(domain);

        for (const domain of domains) {
            const wipeDom = (suffix: string) => {
                document.cookie = `${name}=; Domain=${domain}; Path=/; Max-Age=0${expire}${sameSite}${suffix}`;
            };
            wipeDom(https ? '; Secure' : '');
            wipeDom('; Secure');
            wipeDom('');
        }
    }

    private getLastTenantCookieDomain(): string | null {
        const d = (environment as { lastTenantCookieDomain?: string }).lastTenantCookieDomain?.trim();
        return d || null;
    }

    readLastTenantCookie(): string | null {
        const name = (environment as { lastTenantCookieName?: string }).lastTenantCookieName;
        if (!name || typeof document === 'undefined') return null;
        const prefix = `${name}=`;
        for (const part of document.cookie.split(';')) {
            const c = part.trim();
            if (c.startsWith(prefix)) {
                return this.normalizeTenantIdForCookie(decodeURIComponent(c.slice(prefix.length)));
            }
        }
        return null;
    }

    private normalizeTenantIdForCookie(raw: string): string | null {
        const s = String(raw).trim().toLowerCase();
        if (!s || !/^[a-z0-9_-]+$/.test(s)) return null;
        return s;
    }

    /**
     * Tras validar credenciales, devuelve las cuentas activas asociadas al correo (mismo email en varios tenants).
     */
    resolveLoginAccounts(email: string, password: string): Observable<LoginAccountOption[]> {
        return this.http.post<{ data: LoginAccountOption[] }>(`${this.API_URL}/auth/verify-users-by-email-and-password`, { email, password }).pipe(map(res => res?.data ?? []));
    }

    login(email: string, password: string, redirectTo?: string, selectedTenantId?: string | null) {
        const unified = this.usesUnifiedLoginHost();
        const fromUrl = this.getTenantIdFromUrl();
        const tenantFromUrl = unified ? null : fromUrl;

        const picked = selectedTenantId != null && String(selectedTenantId).trim() !== '' ? String(selectedTenantId).trim().toLowerCase() : '';
        const tenantId = (picked || (tenantFromUrl ? String(tenantFromUrl).trim().toLowerCase() : '')).trim();

        if (!tenantId) {
            return throwError(() => ({
                error: {
                    title: 'Comercio requerido',
                    message: 'Seleccione la cuenta con la que desea ingresar.',
                },
            }));
        }

        const signinHeaders = new HttpHeaders({ tenantid: tenantId });

        return (
            this.http
                .post<AuthResponseData>(
                    `${this.API_URL}/auth/signin`,
                    {
                        email,
                        password,
                        tenantId,
                    },
                    { headers: signinHeaders },
                )
                // return this.http.post<AuthResponseData>("/api/auth/signin", { email, password })
                .pipe(
                    tap(resData => {
                        const user = this.userFromAuthResponse(resData);

                        this.userService.user = user;
                        localStorage.setItem('userData', JSON.stringify(user));
                        localStorage.setItem('user_hash', resData.user_hash);
                        this.setLastTenantCookie(resData.tenantId);

                        const subdomainBase = (environment as { tenantSubdomainBaseUrl?: string }).tenantSubdomainBaseUrl;
                        if (this.usesUnifiedLoginHost() && subdomainBase && resData.tenantId) {
                            const payload = btoa(encodeURIComponent(JSON.stringify(user)));
                            const params = new URLSearchParams();
                            params.set('d', payload);
                            params.set('h', resData.user_hash || '');
                            params.set('t', String(Date.now()));
                            if (redirectTo) params.set('r', redirectTo);
                            const tenantHost = `${String(resData.tenantId).trim().toLowerCase()}.${String(subdomainBase).trim().toLowerCase()}`;
                            const protocol = environment.production ? 'https' : window.location.protocol.replace(':', '');
                            const port = environment.production ? '' : window.location.port ? `:${window.location.port}` : '';
                            /** La sesión vive en el subdominio del tenant; no dejar userData en el host de login unificado. */
                            localStorage.removeItem('userData');
                            localStorage.removeItem('user_hash');
                            this.paymentRenewalAlertService.clearAllRuntimeHides();
                            this.userService.user = null;
                            window.location.href = `${protocol}://${tenantHost}${port}/auth/login-handoff#${params.toString()}`;
                            return;
                        }

                        this.onRedirectTo(redirectTo);
                        this.autoLogout(+resData.expiresIn * 1000);
                        console.log('user_hash', resData.user_hash);
                        this.commerceService.obtainCommerce();
                        this.plansService.obtainPlansBundle();
                        this.fiscalSellerStorageService.ensureRegisteredIfRequired();
                        this.igtfPolicyService.load(); // FINA-3330
                        this.salesChannelsService.loadSalesChannelsInfo();
                        this.commerceService.obtainExchangeRateBCV$();

                        // FINA-2529: cargar el estado del onboarding (HTTP) y abrir la
                        // sincronización en tiempo real (WebSocket) tras el login exitoso.
                        this.onboardingService.loadInitialState();
                        this.onboardingService.initializeRealTimeUpdates();
                        // Fina Pago: cobros por el QR fijo del comercio (bandeja del POS) por el mismo socket.
                        this.finaPaymentsIncomingService.start();

                        if (user?.showNews) this.newsService.loadNews();

                        this.getTenant$().subscribe({
                            next: (res: any) => {
                                this.userService.tenant = res.data;
                                const commerceInfo = res.data?.commerceInfo || {};

                                this.analyticsService.analyticsIdentify(user._id, user.tenantId, {
                                    tenantId: user.tenantId,
                                    email: user.email,
                                    name: user.name,
                                    lastname: user.lastName,
                                    db_id: user._id,
                                    phone: user.phone,
                                    birthday: user.birthday,
                                    industry: commerceInfo.industry || null,
                                    category_business: commerceInfo.categoryBusiness || null,
                                });

                                this.analyticsService.logBankApisFlag('login:group', {
                                    groupType: 'tenant',
                                    groupKey: res.data.tenantId,
                                    identifyTenantId: user.tenantId,
                                });
                                posthog.group('tenant', res.data.tenantId, {
                                    tenantId: res.data.tenantId,
                                    companyName: res.data.companyName,
                                    usePreferentialRate: res.data.usePreferentialRate,
                                    exchangeRateMode: res.data.exchangeRateMode,
                                    useIGTF: res.data.useIGTF,
                                    useIVA: res.data.useIVA,
                                    createdAt: res.data.createdAt,
                                    address: res.data.address,
                                    ci: res.data.dni,
                                    phone: res.data.phone,
                                    ownerId: res.data.ownerId,
                                    plan: res.plan,
                                    industry: commerceInfo.industry || null,
                                    category_business: commerceInfo.categoryBusiness || null,
                                    ...casheaGroupProps(res.data),
                                });
                                this.analyticsService.reloadFeatureFlags();
                                this.analyticsService.analyticsCapture('user logged in', { login_type: 'login' });
                            },
                            error: (err: any) => {
                                if (err?.error?.title && err?.error?.message) this.toastService.error(err.error.title, err.error.message);
                                else console.error(err);
                                this.logout();
                            },
                        });
                    }),
                )
        );
    }

    /**
     * Canjea el código del enlace de soporte (panel admin) por una sesión en este comercio y la
     * arranca igual que `login-handoff`: guarda la sesión y re-hidrata con `autoLogin`.
     */
    loginWithSupportCode$(code: string): Observable<void> {
        const tenantId = this.getTenantIdFromUrl();
        if (!code || !tenantId) {
            return throwError(() => ({ error: { title: 'Enlace inválido', message: 'Genera un nuevo enlace de soporte desde el panel de administración.' } }));
        }

        const headers = new HttpHeaders({ tenantid: String(tenantId).trim().toLowerCase() });
        return this.http.post<AuthResponseData & { supportSession: SupportSessionInfo }>(`${this.API_URL}/auth/support-sessions/redeem`, { code }, { headers }).pipe(
            tap(resData => {
                const user = this.userFromAuthResponse(resData);
                localStorage.setItem('userData', JSON.stringify(user));
                // `autoLogin` exige un user_hash; soporte no se identifica en Intercom.
                localStorage.setItem('user_hash', 'null');
                storeSupportSession(resData.supportSession);
                this.autoLogin();
            }),
            map(() => undefined),
        );
    }

    /** Usuario de sesión a partir de la respuesta de `signin` o del canje de soporte. */
    private userFromAuthResponse(resData: AuthResponseData): User {
        const expirationDate = new Date(new Date().getTime() + +resData.expiresIn * 1000);
        return new User(
            resData._id,
            resData.email,
            resData.name,
            resData.lastName,
            resData.tenantId,
            resData.token,
            expirationDate,
            resData.roles,
            resData.phone,
            resData.birthday,
            resData.location,
            resData.locations,
            resData.enabled,
            resData.status,
            resData.permissions,
            resData.showNews,
            resData.instructionsNotToShow,
            resData.salePreferences,
            // FINA-2062: módulo Créditos solo para tenant con crédito asignado + solo owner.
            !!resData.creditAccess,
            !!resData.isOwner,
            resData.instructionsViews ?? {},
        );
    }

    isSupportSession(): boolean {
        return !!readSupportSession();
    }

    onRedirectTo(redirectTo: string) {
        if (redirectTo) {
            if (redirectTo === 'pagar') this.router.navigate(['comercio/plans'], { queryParams: { pagar: true } });
        } else {
            this.router.navigate(['/dashboard']);
        }
    }

    logout() {
        this.clearLastTenantCookie();
        // FINA-2529: cerrar el socket del onboarding y limpiar el estado para no dejar
        // una conexión abierta con un tenant obsoleto tras cerrar sesión.
        // Fina Pago: antes del teardown (que cierra el socket) para no dejar toasts de un tenant obsoleto.
        this.finaPaymentsIncomingService.stop();
        this.onboardingService.teardown();
        // Reinicia el estado transitorio de métricas del onboarding (timers de blackout + skips) para que no se
        // filtren al próximo usuario que inicie sesión en la misma pestaña (el servicio es singleton).
        this.onboardingAnalyticsService.reset();
        this.userService.user = null;
        this.userService.tenant = null;
        localStorage.removeItem('userData');
        localStorage.removeItem('user_hash');
        clearSupportSession();
        // El ocultamiento del banner de renovación solo debe vivir durante la sesión; al cerrarla,
        // limpiamos los flags para que reaparezca en el próximo inicio de sesión (FINA-2046).
        this.paymentRenewalAlertService.clearAllRuntimeHides();
        // Mismo criterio para el modal bloqueante de "Registrar pago": su descarte se guarda en
        // sessionStorage (clave 'paymentModalDismissed', ver PAYMENT_MODAL_DISMISSED_KEY en
        // layout-dashboard.component.ts). Al cerrar sesión lo limpiamos para que el modal vuelva a
        // evaluarse en la próxima sesión (FINA-2111).
        // Igual con la X de los banners de créditos del Home (FINA-3378).
        clearBannerHides();
        // Y la marca de la bienvenida de Fina Presta del inicio de sesión y el vigía global de
        // «Pagaste» / «Subiste de nivel», con su revisión en vuelo (FINA-3378).
        clearLoginModalChecked();
        this.lineMilestone.reset();
        // ADM-827: el estado de la solicitud gratuita es del comercio que cierra sesión.
        this.controlNumbersStatus.reset();
        try {
            window.sessionStorage?.removeItem('paymentModalDismissed');
        } catch {
            // sessionStorage puede no estar disponible (modo privado/restringido); el cierre de
            // sesión debe continuar igualmente.
        }

        if (this.tokenExpirationTimer) {
            clearTimeout(this.tokenExpirationTimer);
            this.tokenExpirationTimer = null;
        }

        posthog.reset();

        const unifiedEntry = this.getUnifiedLoginEntryUrlForLogout();
        if (unifiedEntry) {
            window.location.replace(unifiedEntry);
            return;
        }

        this.router.navigate(['auth/login'], {
            queryParams: { expired: 'true' },
            replaceUrl: true,
        });
    }

    /**
     * Si el usuario está en un subdominio tenant y existe login unificado, URL absoluta del login central
     * (primer host de `unifiedLoginHostnames`, típicamente `login.fina.business` o `login.finapartner.com`).
     */
    private getUnifiedLoginEntryUrlForLogout(): string | null {
        const list = ((environment as { unifiedLoginHostnames?: string[] }).unifiedLoginHostnames || []).map(h => String(h).trim()).filter(Boolean);
        if (!list.length) return null;

        const cur = window.location.hostname.toLowerCase();
        if (list.some(h => h.toLowerCase() === cur)) return null;

        const path = '/auth/login?expired=true';
        const domain = list[0];
        return `https://${domain}${path}`;
    }

    /**
     * En host de entrada unificado no debe persistir sesión en localStorage (solo cookie de último tenant + handoff).
     * Evita autologin residual en el host de login tras el redirect al subdominio del tenant.
     */
    clearStaleSessionOnUnifiedLoginHost(): void {
        if (typeof window === 'undefined' || !this.usesUnifiedLoginHost()) return;
        localStorage.removeItem('userData');
        localStorage.removeItem('user_hash');
        this.userService.user = null;
    }

    autoLogout(expirationDuration: number) {
        if (this.tokenExpirationTimer) {
            clearTimeout(this.tokenExpirationTimer);
        }

        this.tokenExpirationTimer = setTimeout(() => {
            this.logout();
            this.toastService.error('Error de Usuario', 'Expiró la sesión');
        }, expirationDuration);
    }

    autoLogin() {
        const raw = localStorage.getItem('userData');
        const userHash = localStorage.getItem('user_hash');
        if (!raw || !userHash) return;
        let userData: any;
        try {
            userData = JSON.parse(raw);
        } catch {
            localStorage.removeItem('userData');
            localStorage.removeItem('user_hash');
            return;
        }
        if (!userData || !userData._token) return;

        // Check if token has expired based on timestamp
        const expirationDate = new Date(userData._tokenExpirationDate);
        if (expirationDate <= new Date()) {
            this.logout();
            this.toastService.error('Error de Usuario', 'Expiró la sesión');
            return;
        }

        const loadedUser = new User(
            userData._id,
            userData.email,
            userData.name,
            userData.lastName,
            userData.tenantId,
            userData._token,
            new Date(userData._tokenExpirationDate),
            userData.roles,
            userData.phone,
            userData.birthday,
            userData.location,
            userData.locations,
            userData.enabled,
            userData.status,
            userData.permissions,
            userData.showNews,
            userData.instructionsNotToShow,
            userData.salePreferences,
            !!userData.creditAccess,
            !!userData.isOwner,
            userData.instructionsViews ?? {},
        );
        // VER SI SE PUEDE SIMPLIFICAR ELIMINANDO LA VARIABLE AUXILIAR USERDATA

        if (loadedUser.token) {
            this.userService.user = loadedUser;

            this.verifyToken$().subscribe({
                next: (res: any) => {
                    this.userService.user = new User(
                        res.data._id,
                        res.data.email,
                        res.data.name,
                        res.data.lastName,
                        res.data.tenantId,
                        userData._token,
                        new Date(userData._tokenExpirationDate),
                        res.data.roles,
                        res.data.phone,
                        res.data.birthday,
                        res.data.location,
                        res.data.locations,
                        res.data.enabled,
                        res.data.status,
                        res.data.permissions,
                        res.data.showNews,
                        res.data.instructionsNotToShow,
                        res.data.salePreferences,
                        !!res.data.creditAccess,
                        !!res.data.isOwner,
                        res.data.instructionsViews ?? {},
                    );
                    // FINA-2062: persistir el user actualizado en localStorage para que el
                    // siguiente refresh use los flags frescos (creditAccess/isOwner) y no haya
                    // flicker de blur al renderear con userData viejo. signIn ya lo persiste,
                    // pero este branch es el de re-hidratación post-refresh — sin esto, el
                    // localStorage queda fijo con los flags del momento del login.
                    const refreshedUser = this.userService.user$.value;
                    if (refreshedUser) {
                        localStorage.setItem('userData', JSON.stringify(refreshedUser));
                    }
                    const expirationDuration = new Date(userData._tokenExpirationDate).getTime() - new Date().getTime();
                    this.commerceService.obtainCommerce();
                    this.commerceService.obtainExchangeRateBCV$();
                    this.plansService.obtainPlansBundle();
                    this.fiscalSellerStorageService.ensureRegisteredIfRequired();
                    this.igtfPolicyService.load(); // FINA-3330
                    this.autoLogout(expirationDuration);
                    this.salesChannelsService.loadSalesChannelsInfo();

                    // FINA-2529: mismo arranque de onboarding en la re-hidratación por auto-login.
                    this.onboardingService.loadInitialState();
                    this.onboardingService.initializeRealTimeUpdates();
                    this.finaPaymentsIncomingService.start();

                    if (res?.data?.showNews) this.newsService.loadNews();

                    this.getTenant$().subscribe({
                        next: (res: any) => {
                            this.userService.tenant = res.data;
                            const commerceInfo = res.data?.commerceInfo || {};

                            this.analyticsService.analyticsIdentify(res.data._id, res.data.tenantId, {
                                tenantId: res.data.tenantId,
                                email: res.data.email,
                                name: res.data.name,
                                lastname: res.data.lastName,
                                db_id: res.data._id,
                                phone: res.data.phone,
                                birthday: res.data.birthday,
                                industry: commerceInfo.industry || null,
                                category_business: commerceInfo.categoryBusiness || null,
                            });

                            this.analyticsService.logBankApisFlag('autologin:group', {
                                groupType: 'tenant',
                                groupKey: res.data.tenantId,
                                identifyUserId: res.data._id,
                            });
                            // Solo lo que cambia seguido: así los flags por productos de Cashea no esperan a un login con contraseña.
                            posthog.group('tenant', res.data.tenantId, casheaGroupProps(res.data));
                            this.analyticsService.reloadFeatureFlags();
                            this.analyticsService.analyticsCapture('user logged in', { login_type: 'autologin' });
                        },
                        error: (err: any) => {
                            if (err?.error?.title && err?.error?.message) this.toastService.error(err.error.title, err.error.message);
                            else console.error(err);
                            this.logout();
                        },
                    });
                },
                error: (err: any) => {
                    if (err?.error?.title && err?.error?.message) this.toastService.error(err.error.title, err.error.message);
                    else console.error(err);
                    this.logout();
                },
            });
        } else {
            this.logout();
        }
    }

    changePassword$(data: any) {
        return this.http.post<any>(`${this.API_URL}/auth/changePassword`, data).pipe(
            tap(res => {
                if (res?.token) this.replaceSessionToken(res.token);
            }),
        );
    }

    /** Cambiar la contraseña revoca los tokens anteriores; la API devuelve uno nuevo para esta sesión con el mismo vencimiento. */
    private replaceSessionToken(token: string) {
        const current = this.userService.user$.value;
        if (!current) return;
        const updated: User = Object.assign(Object.create(User.prototype), current, { _token: token });
        this.userService.user = updated;
        localStorage.setItem('userData', JSON.stringify(updated));
    }

    // Para verificar que el token que se encuentra
    getTenant$() {
        return this.http.get<any>(`${this.API_URL}/tenants/tenantId`);
    }
    // Para verificar que el token que se encuentra
    private verifyToken$() {
        return this.http.post<any>(`${this.API_URL}/auth/verified-user`, {});
    }

    private checkSession() {
        const raw = localStorage.getItem('userData');
        if (!raw) return;
        let userData: any;
        try {
            userData = JSON.parse(raw);
        } catch {
            return;
        }
        if (userData && new Date(userData._tokenExpirationDate) <= new Date()) {
            this.logout();
            this.toastService.error('Error de Usuario', 'Expiró la sesión');
        }
    }

    getUserHash(): string | null {
        return localStorage.getItem('user_hash');
    }

    getTenantIdFromUrl(): string | null {
        // Extract subdomain from the current URL
        const hostname = window.location.hostname;
        let name = null;

        // IPv4 addresses have no subdomain — skip extraction entirely
        if (/^(\d{1,3}\.){3}\d{1,3}$/.test(hostname)) return null;

        // Several approaches to extract the subdomain
        if (hostname.includes('.')) {
            // Get the first part of the domain (everything before the first dot)
            const subdomain = hostname.split('.')[0];

            // If it's not 'www' or 'localhost', use it as the name
            if (subdomain !== 'www' && subdomain !== 'localhost') {
                name = subdomain;
            } else if (hostname.split('.').length > 2) {
                // If subdomain is www but there might be another subdomain
                name = hostname.split('.')[1];
            }
        }

        // If no subdomain was found or it's an IP address, check URL path
        if (!name) {
            const pathParts = window.location.pathname.split('/').filter(part => part);
            if (pathParts.length > 0) {
                const first = pathParts[0].toLowerCase();
                const reservedFirstSegments = new Set([
                    'auth',
                    'error',
                    'legal',
                    'dashboard',
                    'inventario',
                    'pos',
                    'cuenta',
                    'accounts',
                    'expenses',
                    'comercio',
                    'financial-summary',
                    'marketing',
                    'statistics',
                    'productos-bancarios',
                    'initial-commerce',
                    'pipes',
                ]);
                if (!reservedFirstSegments.has(first)) {
                    name = pathParts[0];
                }
            }
        }

        return name;
    }
}
